// Building tiles cleared from under raised corridors (B-009, decision D-023). The terrain raster paints 20 m building
// tiles beside and across ~9 m subway corridors; where a raised structure's solid enters a building tile's prism no
// draw order is right for the pair, so the city stage drops that building (its tile keeps its `ground/urban` ground).
//
// A raised structure's volume, per 10 m corridor module, in tile space with heights in units above terrain (0):
// - support volume: the structure's supports from terrain up to the deck, between their outer faces, over the module;
// - deck: the deck/girders at the module height, which a building taller than the deck underside also enters.
// Only raised modules have supports: elevated trestles, embankment berms and the raised part of a ramp. At-grade,
// open-cut and tunnel modules (and a ramp's cut part) stand in no building prism, which starts at terrain.
//
// Pure and dependency-free beyond the composition index, so the data pipeline (tools/tycoon-data) can import it to
// clear the raster at generation time.
import type { CompositionIndex, RampSample, StreamSample } from './compositionIndex.js';
import type { XY } from './composition.js';
import { worldToTile } from './projection.js';

const TILE_M = 20, MODULE_HALF_M = 5, MODULE_HALF = MODULE_HALF_M / TILE_M;
/** Touching faces (to this tolerance, in tiles and units) are adjacent, not intersecting. */
const EPSILON = 1e-6;

/**
 * Structure widths (half, tiles) and deck heights (units, relative to the module height) from the models
 * (tools/tycoon-sprites/models.py, parts.py) as the published atlas-v3 carries them:
 * - trestle: bent columns at ±3.5 m with flanges and the 8.6 m cross girder reach ±4.3 m; 9 m deck; deck proxy
 *   −0.5..+0.37 (proxies.ts LEGACY_PROXIES.trestle);
 * - berm: 12 m earth base with the retaining edge at 5.8 m (≈ ±6 m); body 0..1;
 * - ramp: support faces at DECK_GIRDER_OFFSET 4.3 m, outer face 4.49 m (atlas ramp/support/face); deck flanges
 *   ±4.925 m, −0.594..+0.309 (atlas ramp/deck).
 */
export const STRUCTURE_GEOMETRY = {
  trestle: { supportHalfWidth: 0.215, deckHalfWidth: 0.225, deckZ: [-0.5, 0.37] },
  berm: { supportHalfWidth: 0.3, top: 1 },
  ramp: { supportHalfWidth: 0.2245, deckHalfWidth: 0.24625, deckZ: [-0.5941162944503333, 0.3087127278570641] },
} as const;
export type StructureGeometry = typeof STRUCTURE_GEOMETRY;

/** A convex prism: tile-space footprint (counter-clockwise) and absolute z range in height units. */
export interface StructureVolume { readonly id: string; readonly footprint: readonly XY[]; readonly z: readonly [number, number] }
export type ClearanceIndex = Pick<CompositionIndex, 'corridorSamples' | 'rampSamples' | 'rampAt'>;
export interface ClearedTiles { has(x: number, y: number): boolean; readonly size: number; tiles(): readonly (readonly [number, number])[] }

const finite = (...values: number[]): boolean => values.every(Number.isFinite);
/** Local (longitudinal l, transverse t) box about a module anchor, as tile-space corners (atlas footprint convention). */
function box(anchor: XY, heading: number, l: readonly [number, number], halfWidth: number): XY[] {
  const c = Math.cos(heading), s = Math.sin(heading);
  return ([[l[0], -halfWidth], [l[1], -halfWidth], [l[1], halfWidth], [l[0], halfWidth]] as const)
    .map(([a, t]) => [anchor[0] + a * c - t * s, anchor[1] + a * s + t * c] as XY);
}
const volume = (id: string, footprint: XY[], z: readonly [number, number]): StructureVolume => Object.freeze({ id, footprint: Object.freeze(footprint), z: Object.freeze([z[0], z[1]] as const) });

function corridorVolumes(sample: StreamSample, geometry: StructureGeometry): StructureVolume[] {
  if (!finite(sample.e, sample.n, sample.heading, sample.height)) return [];
  const anchor = worldToTile([sample.e, sample.n]), id = `${sample.corridorId}:${sample.c}`, full = [-MODULE_HALF, MODULE_HALF] as const;
  if (sample.alignment === 'embankment') return [volume(`berm:${id}:support`, box(anchor, sample.heading, full, geometry.berm.supportHalfWidth), [0, geometry.berm.top])];
  if (sample.alignment !== 'elevated') return [];
  const { supportHalfWidth, deckHalfWidth, deckZ } = geometry.trestle, underside = sample.height + deckZ[0];
  return [
    volume(`trestle:${id}:support`, box(anchor, sample.heading, full, supportHalfWidth), [0, underside]),
    volume(`trestle:${id}:deck`, box(anchor, sample.heading, full, deckHalfWidth), [underside, sample.height + deckZ[1]]),
  ];
}

function rampVolumes(sample: RampSample, index: ClearanceIndex, geometry: StructureGeometry): StructureVolume[] {
  const ramp = index.rampAt(sample.corridorId, sample.c);
  if (!ramp || !finite(sample.e, sample.n, sample.heading, sample.height, sample.c) || !(ramp.end > ramp.start)) return [];
  const line = (c: number) => ramp.h0 + (ramp.h1 - ramp.h0) * Math.max(0, Math.min(1, (c - ramp.start) / (ramp.end - ramp.start)));
  // Local +l runs along increasing corridor arclength (the sample heading is the corridor tangent).
  const hStart = line(sample.c - MODULE_HALF_M), hEnd = line(sample.c + MODULE_HALF_M);
  if (!finite(hStart, hEnd) || !(Math.max(hStart, hEnd) > 0)) return [];
  // Support faces stand from terrain up to the rail line where it is above ground (rampWorld.ts clip outline).
  const crossing = hStart === hEnd ? 0 : -MODULE_HALF + 2 * MODULE_HALF * (0 - hStart) / (hEnd - hStart);
  const raised: [number, number] = hStart > 0 && hEnd > 0 ? [-MODULE_HALF, MODULE_HALF] : hStart > 0 ? [-MODULE_HALF, crossing] : [crossing, MODULE_HALF];
  const anchor = worldToTile([sample.e, sample.n]), id = `ramp:${sample.rampId}:${sample.c}`, { supportHalfWidth, deckHalfWidth, deckZ } = geometry.ramp;
  return [
    volume(`${id}:support`, box(anchor, sample.heading, raised, supportHalfWidth), [0, Math.max(hStart, hEnd)]),
    volume(`${id}:deck`, box(anchor, sample.heading, [-MODULE_HALF, MODULE_HALF], deckHalfWidth), [sample.height + deckZ[0], sample.height + deckZ[1]]),
  ];
}

/** Every raised module's support and deck volumes. Malformed samples and ramps contribute nothing. */
export function raisedStructureVolumes(index: ClearanceIndex, geometry: StructureGeometry = STRUCTURE_GEOMETRY): StructureVolume[] {
  return [
    ...index.corridorSamples().flatMap((sample) => corridorVolumes(sample, geometry)),
    ...index.rampSamples().flatMap((sample) => rampVolumes(sample, index, geometry)),
  ];
}

/** Separating-axis test for convex polygons: true only for a positive-area overlap (touching is adjacent). */
function convexOverlap(a: readonly XY[], b: readonly XY[]): boolean {
  for (const polygon of [a, b]) for (let i = 0; i < polygon.length; i += 1) {
    const p = polygon[i]!, q = polygon[(i + 1) % polygon.length]!, nx = q[1] - p[1], ny = p[0] - q[0], tolerance = Math.hypot(nx, ny) * EPSILON;
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for (const v of a) { const d = v[0] * nx + v[1] * ny; minA = Math.min(minA, d); maxA = Math.max(maxA, d); }
    for (const v of b) { const d = v[0] * nx + v[1] * ny; minB = Math.min(minB, d); maxB = Math.max(maxB, d); }
    if (maxA <= minB + tolerance || maxB <= minA + tolerance) return false;
  }
  return true;
}

/** True when a building tile prism (tile square × [0, top]) and a structure volume share positive volume. */
export function buildingIntersects(structure: StructureVolume, tile: readonly [number, number], top: number): boolean {
  if (!(Number.isFinite(top) && top > EPSILON)) return false;
  if (!(structure.z[1] > EPSILON && top > structure.z[0] + EPSILON)) return false;
  const [x, y] = tile;
  return convexOverlap(structure.footprint, [[x, y], [x + 1, y], [x + 1, y + 1], [x, y + 1]]);
}

const key = (x: number, y: number): number => x * 65536 + y;
const frozenTiles = (keys: ReadonlySet<number>): ClearedTiles => {
  const list = Object.freeze([...keys].sort((a, b) => a - b).map((k) => Object.freeze([Math.floor(k / 65536), k % 65536] as const)));
  return Object.freeze({ has: (x: number, y: number) => Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && keys.has(key(x, y)), size: keys.size, tiles: () => list });
};
export const NO_CLEARED_TILES: ClearedTiles = frozenTiles(new Set());

/**
 * Building tiles (by `buildingTopAt`, the building prism's top in units, undefined where there is no building) that
 * intersect any structure volume. Each volume visits only the tiles under its footprint's bounding box.
 */
export function clearedBuildingTiles(volumes: readonly StructureVolume[], buildingTopAt: (x: number, y: number) => number | undefined): ClearedTiles {
  const keys = new Set<number>();
  for (const structure of volumes) {
    const xs = structure.footprint.map(([x]) => x), ys = structure.footprint.map(([, y]) => y);
    for (let y = Math.max(0, Math.floor(Math.min(...ys))); y <= Math.floor(Math.max(...ys)); y += 1) for (let x = Math.max(0, Math.floor(Math.min(...xs))); x <= Math.floor(Math.max(...xs)); x += 1) {
      if (keys.has(key(x, y))) continue;
      const top = buildingTopAt(x, y);
      if (top !== undefined && buildingIntersects(structure, [x, y], top)) keys.add(key(x, y));
    }
  }
  return keys.size === 0 ? NO_CLEARED_TILES : frozenTiles(keys);
}
